import { useRef, useState, useEffect, forwardRef } from "react";
import styles from "./scrolling-cards.module.scss";
import Image from "@/components/Image/image";
import { gsap } from "gsap";
import SbEditable from "storyblok-react";
import classnames from "classnames";
import {
  ScrollingCardStoryblok,
  ScrollingCardsStoryblok,
} from "@/types/component-types-sb";

interface Props {
  blok: ScrollingCardsStoryblok;
  className?: string;
}

const ScrollingCards = (props: Props) => {
  const {
    blok: { items },
    className,
  } = props;
  const [tl] = useState(gsap.timeline({ paused: true, repeat: -1 }));
  const doubledItems = [...(items || []), ...(items || [])];
  const wrapperRef = useRef(null);
  const cardRefs = useRef<HTMLDivElement[]>([]);

  const addToRefs = (el: null | HTMLDivElement) => {
    if (cardRefs.current && el !== null && !cardRefs.current.includes(el)) {
      cardRefs.current.push(el);
    }
  };

  useEffect(() => {
    const boxWidth = 300;
    const boxHeight = 180;
    const wrapWidth = cardRefs.current.length * (boxWidth + 24);
    const viewWrap = gsap.utils.wrap(0, wrapWidth);

    gsap.set(wrapperRef.current, {
      x: -boxWidth,
    });
    cardRefs.current.forEach((card, i) => {
      gsap.set(card, {
        x: i * boxWidth + i * 24,
        width: boxWidth,
        height: boxHeight,
      });
    });

    tl.to(cardRefs.current, {
      x: `+=${wrapWidth}`,
      duration: 50,
      ease: "none",
      modifiers: {
        x: (x) => viewWrap(parseFloat(x)) + "px",
      },
    });
  }, []);

  useEffect(() => {
    tl?.play?.();

    return () => {
      tl.kill();
    };
  }, [cardRefs.current]);

  return doubledItems.length ? (
    // @ts-expect-error
    <SbEditable content={props.blok}>
      <div className={classnames(styles.wrapper, className)}>
        <div className={styles.inner} ref={wrapperRef}>
          {doubledItems.map((item) => (
            <ScrollingCard
              key={item._uid}
              image={item.image}
              title={item.title}
              ref={addToRefs}
            />
          ))}
        </div>
      </div>
    </SbEditable>
  ) : null;
};

const ScrollingCard = forwardRef<HTMLDivElement, ScrollingCardStoryblok>(
  (props, ref) => {
    const { image, title } = props;
    return image?.filename ? (
      <div className={styles.card} ref={ref}>
        <Image
          sizes="200px"
          filename={image.filename}
          alt={image.alt}
          className={styles.cardImage}
        />
        <p className={styles.cardTitle}>{title}</p>
      </div>
    ) : null;
  }
);

export default ScrollingCards;
